import type { Session, User } from '@supabase/supabase-js'
import { useQueryClient } from '@tanstack/react-query'
import { createContext, use, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

import { supabase } from '@/lib/supabase'

interface AuthContextValue {
  session: Session | null
  user: User | null
  /** True until the initial session has been resolved. */
  isLoading: boolean
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

/**
 * Session state for the whole app.
 *
 * Deliberately NOT a TanStack Query — auth is push-based (Supabase emits
 * events on refresh, sign-out and token expiry) and modelling it as a
 * pull-based cache produces a window where the app believes it is signed
 * in and every request 401s.
 *
 * This provider holds session only. Profile and membership data are
 * ordinary queries, because they are ordinary data.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = useQueryClient()

  // A ref, not the `session` state: reading state inside the listener
  // would capture the value from the render that installed it, so the
  // "did the user change?" comparison would always see null.
  const lastUserId = useRef<string | null>(null)

  useEffect(() => {
    let active = true

    // Resolve the existing session before rendering anything gated, so a
    // returning user never sees a flash of the sign-in screen.
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return
      lastUserId.current = data.session?.user.id ?? null
      setSession(data.session)
      setIsLoading(false)
    })

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!active) return

      // Review C7: without this, one family's archive stays in the cache
      // after sign-out and is briefly rendered to whoever signs in next
      // on the same device. On a shared laptop that is a real disclosure,
      // not a theoretical one.
      const nextUserId = nextSession?.user.id ?? null

      // A different user on the same device must not inherit the previous
      // one's cached rows either.
      if (event === 'SIGNED_OUT' || nextUserId !== lastUserId.current) {
        queryClient.clear()
      }

      lastUserId.current = nextUserId
      setSession(nextSession)
      setIsLoading(false)
    })

    return () => {
      active = false
      subscription.unsubscribe()
    }
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      isLoading,
      isAuthenticated: session !== null,
    }),
    [session, isLoading]
  )

  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
